import Color from "color";
import Icon from "../components/Icon";
import { ensureArray } from "./arrayUtilities";
import { readField } from "./objectUtilities";
import { getSelector } from "./generalUtils";
import { deepClone } from "./objectUtilities";

export const dynamicSort = (properties, rf = readField) => {
    return function (a, b) {
        return properties?.reduce((acc, prop) => {
            if (acc !== undefined) return acc;  // If already determined precedence, return it.

            const order = prop.startsWith('-') ? -1 : 1;
            const name = prop.startsWith('-') ? prop.slice(1) : prop;

            if (rf(a, name) < rf(b, name)) {
                return -1 * order;
            }
            if (rf(a, name) > rf(b, name)) {
                return 1 * order;
            }
            return undefined;  // Continue to next property if equal.
        }, undefined) ?? 0;
    };
};
export const dynamicFilter = (array, query, options = {}) => {
    const seenObjects = new Set();
    const { ignoreFields, ...props } = options;
    if (props?.length > 0) {
        throw new Error(`Unknown options to dyanmicFilter ${Object.keys(props).join(', ')}`);
    }

    function matchQuery(item, query, {ignoreFields = []}) {
        if (query === undefined || query === null || query === '') {
            return true;
        }
        if (item === query || (typeof query === 'string' && item?.toString && typeof item?.toString === 'function' && item?.toString?.length === 0 && item?.toString() === query)) {
            return true;
        }

        if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
            if (seenObjects.has(item)) {
                // Avoid infinite recursion for circular references
                return false;
            }
            seenObjects.add(item);

            return Object.keys(item).filter(field => !ignoreFields.includes(field)).some(key => {
                // Allows for dotted notation in `ignoreFields` to target nested fields at the same level as the corresponding object structure.
                const fieldPrefix = key + '.';
                return matchQuery(item[key], query, 
                    {
                        ignoreFields: ignoreFields
                            .filter((field) => field.startsWith(fieldPrefix))
                            .map((field) => field.substring(fieldPrefix.length))
                    }
                );
            });
        } else if (typeof item === 'string' && typeof query === 'string') {
            return item.toLowerCase().includes(query.toLowerCase().trim());
        } else if (Array.isArray(item)) {
            return item.some(i => matchQuery(i, query, ignoreFields));
        }

        return false;
    }
    
    const queries = typeof query === 'string' ? query.trim().split(/\s+/) : [query];
    const result = queries.reduce((filteredResult, query) => {
        seenObjects.clear();
        return filteredResult.filter(item => matchQuery(item, query, {ignoreFields: ensureArray(ignoreFields)}));
    }, array);
    return result;
}

export const complexTableQuery = (array, query, options = {}) => {
    const seenObjects = new Set();
    const { ignoreFields, ...props } = options;

    if (props?.length > 0) {
        throw new Error(`Unknown options to dyanmicFilter ${Object.keys(props).join(', ')}`);
    }

    function matchQuery(item, query, {ignoreFields}) {
        if (query === undefined || query === null || query === '' || (typeof query === 'object' && Object.keys(query).length === 0)) {
            return true;
        }
        if (item === query || (typeof query === 'string' && item?.toString() === query)) {
            return true;
        }

        if (item && Array.isArray(item) && query && Array.isArray(query)) {
            return query.every(q => item.some(i => matchQuery(q, i, ignoreFields)));
        } else if (Array.isArray(item)) {
            return item.some(i => matchQuery(i, query, { ignoreFields }));
        } else if (typeof item === 'object' && item !== null) {
            if (seenObjects.has(item)) {
                // Avoid infinite recursion for circular references
                return false;
            }
            seenObjects.add(item);

            return Object.keys(query).filter(field => !ignoreFields.includes(field)).every(key => {
                // Allows for dotted notation in `ignoreFields` to target nested fields at the same level as the corresponding object structure.
                const fieldPrefix = key + '.';                
                return matchQuery(item[key], query[key], 
                    {
                        ignoreFields: ignoreFields
                            .filter((field) => field.startsWith(fieldPrefix))
                            .map((field) => field.substring(fieldPrefix.length))
                        } 
                    );
                });
            } else if (typeof item === 'string' && typeof query === 'string') {
                return item.toLowerCase().includes(query.toLowerCase().trim());
            }

        return false;
    }

    return array.filter(item => matchQuery(item, query, {ignoreFields: ensureArray(ignoreFields)}));
}

export const makeGridHeader = (cols, sortParams, setSort) => {
    return makeHeader(cols, sortParams, setSort, ({ children, ...props }) => <div {...props}>{children}</div>);
}

export const makeTableHeader = (cols, sortParams, setSort) => {
    return makeHeader(cols, sortParams, setSort, ({ children, ...props }) => <th {...props}>{children}</th>);
}

const makeHeader = (cols, sortParams, setSort, Tag) => {
    const toggleSort = ($event, field) => {
        sortParams = deepClone(sortParams);
        if ($event.ctrlKey) {
            let ascIdx = sortParams.indexOf(field);
            let dscIdx = sortParams.indexOf('-' + field);
            if (ascIdx === -1 && dscIdx === -1) {
                sortParams.push(field);
            } else if (ascIdx === -1) {
                sortParams.splice(dscIdx, 1);
                sortParams.push(field);
            } else {
                sortParams.splice(ascIdx, 1);
                sortParams.push('-' + field);
            }
        } else {
            let idx = sortParams.indexOf(field);
            sortParams.splice(0, sortParams.length);
            if (idx === -1) {
                sortParams.push(field);
            } else {
                sortParams.push('-' + field);
            }
        }
        setSort(sortParams);
    }
    return cols.map(col => {
        if (typeof col.sortfield === 'string' || typeof col.field === 'string') {
            const sortfield = col.sortfield ?? col.field;
            return <Tag key={getSelector(col)} className={col.className} onClick={event => toggleSort(event, sortfield)} style={{ cursor: 'pointer' }}>
            {col.title}
            &nbsp;
            {sortfield && sortParams.includes('-' + sortfield) ? <Icon intent="sortDown"></Icon> : null}
            {sortfield && sortParams.includes(sortfield) ? <Icon intent="sortUp"></Icon> : null}
            {sortfield && !sortParams.includes('-' + sortfield) && !sortParams.includes(sortfield) ? <Icon intent="sort" style={{ color: 'lightgray' }}></Icon> : null}
            </Tag>
        } else {
            return <Tag key={col.id ?? col.field} className={col.className}>
                {col.title}
            </Tag>
        }
    });
}

export const capitalize = (str, lower = false) => (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, match => match.toUpperCase());

export const isDark = (value) => Color(value).isDark();

export const buildColor = (value, selected, factor = 0.4) => {
    if (value) {
        let c = Color(value);
        if (selected) {
            if (c.isDark()) {
                c = c.lighten(factor);
            } else {
                c = c.lighten(-factor);
            }
        }
        return { backgroundColor: c.hex(), color: c.isDark() ? '#ffffff' : '#000000'}
    } else {
        return { backgroundColor: null, color: null };
    }
}
export const buildShadedColor = buildColor;

// export const buildShadedColor = (value, selected, factor = 0.8) => {
//     const darkMode = false;// window.matchMedia('(prefers-color-scheme: dark)').matches
//     if (value) {
//         let backgroundColor = Color(value)[darkMode ? 'darken' : 'lighten'](factor);
//         let color = Color(value)[darkMode ? 'lighten' : 'darken'](factor);
//         if (color.contrast(backgroundColor) < 7) {
//             if (backgroundColor.isDark()) {
//                 color = Color('#ffffff');
//             } else {
//                 color = Color('#000000');
//             }
//         }
//         if (selected) {
//             if (backgroundColor.isDark()) {
//                 color = color.lighten(factor / 2);
//                 backgroundColor = backgroundColor.lighten(factor / 2);
//             } else {
//                 color = color.darken(factor / 2);
//                 backgroundColor = backgroundColor.darken(factor / 2);
//             }
//         }
//         return { backgroundColor: backgroundColor.hex(), color: color.hex() };
//     } else {
//         return { backgroundColor: null, color: null };
//     }
// }